export interface SimpleContentPDFResult {
  isValid: boolean;
  isPolicyDocument: boolean;
  error?: string;
}

class SimpleContentPDFService {
  // 保單內容關鍵字列表
  private readonly policyKeywords = [
    // 英文關鍵字
    'policy', 'insurance', 'insured', 'policyholder', 'coverage', 
    'premium', 'effective', 'expiration', 'beneficiary', 
    'underwriter', 'claim', 'amount', 'contract', 'agreement',
    'protection', 'coverage', 'deductible', 'copay', 'benefits',
    'terms', 'conditions', 'liability', 'health', 'life', 'auto',
    'property', 'casualty', 'disability', 'annuity', 'endowment',
    
    // 中文關鍵字 - 基本保單術語
    '保單', '保險', '保險單', '保險契約', '保險合同', '保險證',
    '要保人', '被保人', '被保險人', '受益人', '保險人', '承保人',
    '保單號碼', '保單號', '保險號碼', '契約號碼', '合同號碼',
    '承保範圍', '保險範圍', '保障範圍', '承保內容', '保險內容',
    '保險金額', '保額', '保障金額', '保險金', '理賠金額',
    '保費', '保險費', '保費金額', '年繳保費', '月繳保費', '季繳保費',
    '理賠', '保險理賠', '理賠申請', '理賠金', '給付', '保險給付',
    
    // 中文關鍵字 - 保險公司
    '富邦', '國泰', '新光', '南山', '中國人壽', '台灣人壽', '全球人壽',
    '元大人壽', '三商美邦', '遠雄人壽', '安聯人壽', '保誠人壽',
    '友邦人壽', '宏泰人壽', '台新人壽', '第一金人壽', '合庫人壽',
    '台灣人壽', '中華郵政', '郵政壽險', '勞保', '健保', '農保',
    
    // 中文關鍵字 - 保險類型
    '人壽保險', '壽險', '終身壽險', '定期壽險', '儲蓄險', '養老險',
    '健康保險', '醫療保險', '醫療險', '重大疾病', '癌症險', '防癌險',
    '意外保險', '意外險', '傷害保險', '傷害險', '旅遊險', '旅遊保險',
    '汽車保險', '車險', '強制險', '第三人責任險', '車體險',
    '火災保險', '火險', '住宅火險', '商業火險', '地震險',
    '責任保險', '責任險', '產品責任險', '公共意外責任險',
    '寵物保險', '寵物險', '寵物醫療險',
    
    // 中文關鍵字 - 保單內容
    '生效日期', '起保日期', '保險期間', '保險期限', '保障期間',
    '失效日期', '終止日期', '到期日期', '滿期日期',
    '等待期間', '等待期', '免責期間', '觀察期間',
    '自負額', '自付額', '免責額', '自負額度',
    '給付項目', '保障項目', '理賠項目', '給付內容',
    '除外責任', '不保事項', '除外事項', '免責事項',
    '保險條款', '契約條款', '合同條款', '保單條款',
    
    // 中文關鍵字 - 常見保單用詞
    '身分證字號', '身分證號', '身分證', '統一編號', '統編',
    '出生日期', '出生年月日', '年齡', '性別', '職業',
    '聯絡地址', '通訊地址', '戶籍地址', '居住地址',
    '聯絡電話', '電話', '手機', '行動電話',
    '電子郵件', 'email', '信箱', '電子信箱',
    '要保書', '投保書', '申請書', '契約書', '合同書',
    '保險建議書', '建議書', '計劃書', '商品說明書',
    
    // 中文關鍵字 - 理賠相關
    '理賠申請書', '理賠申請', '理賠文件', '理賠資料',
    '診斷書', '診斷證明', '醫療證明', '住院證明',
    '收據', '發票', '醫療收據', '費用收據',
    '死亡證明', '除戶證明', '戶籍謄本', '戶籍證明',
    
    // 中文關鍵字 - 其他常見詞彙
    '續保', '續約', '展期', '復效', '停效', '解約',
    '減額繳清', '展期定期', '保單借款', '保單質借',
    '紅利', '分紅', '保單紅利', '分紅保險',
    '現金價值', '解約金', '滿期金', '生存金',
    '身故保險金', '全殘保險金', '重大疾病保險金',
    '住院醫療', '門診醫療', '手術醫療', '實支實付',
    '日額給付', '定額給付', '限額給付', '無上限',
    
    // 中文關鍵字 - 保險商品名稱常見詞
    '終身', '定期', '還本', '增額', '變額', '萬能',
    '分紅', '不分紅', '傳統型', '投資型', '連結型',
    '健康', '醫療', '防癌', '重大疾病', '長期照護',
    '意外', '傷害', '旅遊', '汽車', '機車', '住宅',
    '火災', '地震', '颱風', '洪水', '竊盜', '責任',
    '寵物', '高爾夫', '運動', '職業', '團體', '個人'
  ];

  // 簡化版PDF驗證 - 基於文件名和基本檢查
  async validatePDF(file: File): Promise<SimpleContentPDFResult> {
    try {
      console.log('使用簡化版PDF驗證服務:', file.name);
      
      // 檢查文件格式
      if (!this.isPDFFile(file)) {
        return {
          isValid: false,
          isPolicyDocument: false,
          error: '請上傳 PDF 格式的檔案。'
        };
      }

      // 檢查文件大小
      if (file.size === 0) {
        return {
          isValid: false,
          isPolicyDocument: false,
          error: '檔案為空，請檢查檔案是否完整。 / File is empty, please check if the file is complete.'
        };
      }

      // 檢查文件大小限制（10MB）
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        return {
          isValid: false,
          isPolicyDocument: false,
          error: '檔案大小不能超過10MB。 / File size cannot exceed 10MB.'
        };
      }

      // 檢查文件名是否包含保單關鍵字
      const fileName = file.name.toLowerCase();
      const matchedKeywords = this.checkPolicyKeywords(fileName);
      
      console.log(`文件名: ${file.name}`);
      console.log(`匹配的關鍵字數量: ${matchedKeywords.length}`);
      console.log(`匹配的關鍵字: ${matchedKeywords.join(', ')}`);

      // 如果文件名包含保單關鍵字，認為是保單文件
      if (matchedKeywords.length >= 1) {
        return {
          isValid: true,
          isPolicyDocument: true
        };
      }

      // 如果文件名不包含關鍵字，但用戶選擇了上傳保單功能，我們信任用戶的選擇
      console.log('文件名不包含保單關鍵字，但信任用戶選擇');
      return {
        isValid: true,
        isPolicyDocument: true
      };

    } catch (error: any) {
      console.error('簡化版PDF驗證失敗:', error);
      return {
        isValid: false,
        isPolicyDocument: false,
        error: '文件驗證失敗，請稍後再試。 / File validation failed, please try again later.'
      };
    }
  }

  // 檢查保單關鍵字
  private checkPolicyKeywords(text: string): string[] {
    const lowerText = text.toLowerCase();
    const matchedKeywords: string[] = [];
    
    for (const keyword of this.policyKeywords) {
      if (lowerText.includes(keyword.toLowerCase())) {
        matchedKeywords.push(keyword);
      }
    }
    
    return matchedKeywords;
  }

  // 檢查是否為PDF文件
  private isPDFFile(file: File): boolean {
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  }

  // 顯示錯誤彈窗
  showErrorAlert(message: string): void {
    alert(message);
  }

  // 顯示成功彈窗
  showSuccessAlert(message: string): void {
    alert(message);
  }
}

export const simpleContentPDFService = new SimpleContentPDFService();
